import { useMemo } from 'react';
import { useClinicContext } from '../context/ClinicContext.jsx';

/**
 * Client-side access gate (UX only — the BFF permission map is the real boundary).
 *
 *   const { can, isOwner, isAdmin } = useAccess();
 *   if (can('people.manage')) { … }
 *
 * THIS NO LONGER PARSES THE TOKEN, and both reasons are load-bearing under
 * multi-clinic (docs/CLINIC_MULTI_CLINIC_UX_REV2.md §2.4):
 *
 * 1. The token's `perm.*` claims are FLAT. Keycloak expands every composite the
 *    person holds into one array with no clinic attached, so a person who works at
 *    two clinics arrives holding the union of both — and reading that array would
 *    show a receptionist at clinic B the sidebar of an admin at clinic A. The
 *    server resolves permissions per clinic; the client renders what it is told.
 * 2. It memoized on `[]`, so grants never refreshed inside a mounted tree. That was
 *    survivable when the only way to change them was to sign out; it is not
 *    survivable when switching clinics changes them.
 *
 * `isOwner`/`isAdmin` likewise come from the MEMBERSHIP, not from the realm-wide
 * `clinic_owner`/`clinic_admin` roles — those are not per-clinic and would follow a
 * founder into every other clinic they happen to work at.
 */
export function useAccess() {
  const { access, activeMembership, state } = useClinicContext();

  return useMemo(() => {
    const tier = access?.tier || activeMembership?.tier || null;
    const isOwner = tier === 'OWNER';
    const isAdmin = isOwner || tier === 'ADMIN';

    const list = access?.permissions || [];
    const all = list.includes('ALL');
    const perms = new Set(all ? [] : list);

    // Who this person is AS A PRACTITIONER at this clinic, or null. Resolved by the
    // server from an ACTIVE clinic_doctors row — `activeMembership.kinds` cannot
    // answer it, because a pending join still carries the PRACTITIONER kind.
    const practitionerId = access?.practitionerId || null;

    return {
      isOwner,
      isAdmin,
      practitionerId,
      /** A doctor who also works this panel — the manager who sees patients. */
      isPractitioner: Boolean(practitionerId),
      /** `ALL` is a token in the list, not a magic role name. */
      can: (key) => all || perms.has(key),
      /** True for any one of several keys — «عملیات» needs three. */
      canAny: (...keys) => all || keys.flat().some((k) => perms.has(k)),
      perms,
      /**
       * Enterable, but holding nothing. A real state (§5.9.2), distinct from
       * "still loading", which must never render as a permission verdict.
       */
      noPermissions: Boolean(access?.noPermissions),
      ready: state === 'ready' && Boolean(access),
      roles: access?.roles || activeMembership?.roles || [],
      tier,
    };
  }, [access, activeMembership, state]);
}

export default useAccess;
